/**
 * The Team ladder (decision 7, 2026-10-19): the one control for how far the
 * Team goes on its own. Three steps, each saying what a person still does.
 * Picking one sets every Agent's rung and "Merge automatically" at Save
 * (`PATCH /api/desks/:id/charter` with `autonomy`); per-Agent rungs are not
 * shown here. A Team whose Agents were set one by one (God Mode's override)
 * reads "Custom (override)" until a step is picked.
 */
import { TEAM_AUTONOMY_STEPS, type TeamAutonomy } from "@yagni/shared";

import { ReviewAppInstallNote } from "../../connections/ReviewAppInstallNote.js";
import { LADDER_CUSTOM, LADDER_CUSTOM_NOTE, LADDER_LABEL, LADDER_STEPS } from "../copy.js";
import { attachedRepos } from "../useTeamPage.js";
import type { SettingsCtx } from "./context.js";
import { Field, Seg } from "./fields.js";

const CUSTOM = "custom";

export function LadderField({ ctx }: { ctx: SettingsCtx }) {
  const step = ctx.team.autonomy;
  const repos = attachedRepos(ctx.page.attachments);
  return (
    <Field
      label={LADDER_LABEL}
      top
      testId="settings-team-ladder"
      help={
        step === null ? (
          <span data-testid="settings-team-ladder-custom">
            <b>{LADDER_CUSTOM}.</b> {LADDER_CUSTOM_NOTE}
          </span>
        ) : (
          <>
            <span data-testid="settings-team-ladder-help">{LADDER_STEPS[step].person}</span>
            {/* Informs before Save; the Save itself refuses without YAGNI Review. */}
            <ReviewAppInstallNote
              repos={repos}
              enabled={step === "autonomous_auto_merge"}
              variant="help"
              testId="settings-team-ladder-review-app"
            />
          </>
        )
      }
    >
      <span>
        <Seg<TeamAutonomy | typeof CUSTOM>
          label={LADDER_LABEL}
          value={step ?? CUSTOM}
          disabled={ctx.readOnly || ctx.busy}
          testId="settings-team-ladder-step"
          options={TEAM_AUTONOMY_STEPS.map((value) => ({
            value,
            label: LADDER_STEPS[value].label,
          }))}
          onChange={(value) => {
            if (value === CUSTOM) return;
            ctx.setTeam((team) => ({ ...team, autonomy: value }));
          }}
        />
      </span>
    </Field>
  );
}
